import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { InfoAlert } from 'smartsuite-ui';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import GaugeComposedBarLineGraph from './GaugeComposedBarLineGraph';
import {
    hasMetricSources,
    getMetricSourceForRequest,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import { getGaugeFields } from '../../utils/gaugeDataFormatUtils';
import { getFormattedTimeFromMilliseconds } from '../../utils/dataFormatUtils';

// eslint-disable-next-line complexity
function getMetricSource({ metricRequests, metrics, configuration }) {
    const barRequest = metricRequests.find(
        (request) => request.id === configuration.barMetricRequestId
    );
    const lineRequest = metricRequests.find(
        (request) => request.id === configuration.lineMetricRequestId
    );
    if (
        !barRequest ||
        barRequest.metrics.length === 0 ||
        !lineRequest ||
        lineRequest.metrics.length === 0 ||
        barRequest.groupBy[0] !== lineRequest.groupBy[0]
    ) {
        return { errorKey: 'invalidMetricRequests' };
    }

    const barMetricSource = getMetricSourceForRequest(metrics, barRequest);
    const lineMetricSource = getMetricSourceForRequest(metrics, lineRequest);
    if (!barMetricSource || !lineMetricSource) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized([barMetricSource, lineMetricSource])) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources([barMetricSource, lineMetricSource], false)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [barMetricSource, lineMetricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { barRequest, lineRequest, barMetricSource, lineMetricSource };
}

// eslint-disable-next-line complexity
export default function GaugeComposedBarLineGraphWrapper(props) {
    const {
        barRequest,
        lineRequest,
        barMetricSource,
        lineMetricSource,
        metricSourceErrors,
        errorKey,
    } = getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const dataFormatter = props.configuration.isTime
        ? getFormattedTimeFromMilliseconds
        : props.numberDecimalFormatter;

    const barFields = getGaugeFields(
        barRequest,
        barMetricSource,
        props.numberDecimalFormatter,
        'bar'
    );
    const lineFields = getGaugeFields(
        lineRequest,
        lineMetricSource,
        props.numberDecimalFormatter,
        'line'
    );

    const formattedData = _(barFields.formattedData)
        .merge(lineFields.formattedData)
        .values()
        .value();

    return (
        <>
            {barMetricSource.isTruncated && (
                <InfoAlert>
                    {`${props.dashboardLabels.truncationWarning} ${barRequest.truncationThreshold}`}
                </InfoAlert>
            )}
            <div className="chart-container">
                <GaugeComposedBarLineGraph
                    {...props}
                    barDataLabels={barFields.dataLabels}
                    chartProperties={props.configuration}
                    dataFormatter={dataFormatter}
                    dataPoints={formattedData}
                    firstGroupKey={barFields.groupBy}
                    lineDataLabels={lineFields.dataLabels}
                />
            </div>
        </>
    );
}

GaugeComposedBarLineGraphWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
};
